import { useMemo, useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Wheat, Tractor, PiggyBank, Wrench, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  computeCampaignKPIs, computeMonthlyLoan, distributeAnnualAmount,
  formatEur, MONTH_LABELS
} from '../../utils/agriEngine';
import type {
  AgriCampaign, AgriCampaignCharge, AgriSubsidy,
  AgriLivestockSale, AgriLivestockCharge, AgriEquipment
} from '../../utils/agriEngine';
import {
  useAgriCampaigns, useAllAgriCharges, useAgriSubsidies,
  useAgriLivestockHerds, useAgriEquipment
} from '../../hooks/useAgriculture';

interface Props {
  year: number;
}

function useAllLivestockData(year: number) {
  const [sales, setSales] = useState<AgriLivestockSale[]>([]);
  const [charges, setCharges] = useState<AgriLivestockCharge[]>([]);

  const load = useCallback(async () => {
    const { data: sData } = await supabase
      .from('agri_livestock_sales')
      .select('*')
      .eq('campaign_year', year);
    const { data: cData } = await supabase
      .from('agri_livestock_charges')
      .select('*')
      .eq('campaign_year', year);
    setSales(sData || []);
    setCharges(cData || []);
  }, [year]);

  useEffect(() => { load(); }, [load]);
  return { sales, charges };
}

function Bar({ value, max, positive }: { value: number; max: number; positive: boolean }) {
  const pct = max > 0 ? Math.min(100, Math.abs(value) / max * 100) : 0;
  return (
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${positive ? 'bg-green-500' : 'bg-red-400'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function AgriDashboardTab({ year }: Props) {
  const { campaigns } = useAgriCampaigns(year);
  const { chargesByCampaign } = useAllAgriCharges(year);
  const { subsidies } = useAgriSubsidies(year);
  const { herds } = useAgriLivestockHerds();
  const { equipment } = useAgriEquipment();
  const { sales: allSales, charges: allLivestockCharges } = useAllLivestockData(year);

  const data = useMemo(() => {
    const monthly = Array.from({ length: 12 }, (_, i) => ({
      month: i,
      label: MONTH_LABELS[i],
      inflows: 0,
      outflows: 0,
      net: 0,
      cumulative: 0,
    }));

    let totalCropRevenue = 0;
    let totalCropCharges = 0;

    campaigns.forEach((c: AgriCampaign) => {
      const charges: AgriCampaignCharge[] = chargesByCampaign[c.id] || [];
      const kpi = computeCampaignKPIs(c, charges);
      totalCropRevenue += kpi.gross_revenue;
      totalCropCharges += kpi.total_charges;

      if (c.harvest_date) {
        const m = new Date(c.harvest_date).getMonth();
        monthly[m].inflows += kpi.gross_revenue;
      }
      charges.forEach(ch => {
        if (ch.charge_date) {
          const m = new Date(ch.charge_date).getMonth();
          monthly[m].outflows += ch.amount;
        }
      });
    });

    let totalSubsidiesReceived = 0;
    let totalSubsidiesPending = 0;

    subsidies.forEach((s: AgriSubsidy) => {
      if (s.status === 'recu') {
        totalSubsidiesReceived += s.amount;
        if (s.theoretical_payment_date) {
          const m = new Date(s.theoretical_payment_date).getMonth();
          monthly[m].inflows += s.amount;
        }
      } else if (s.status === 'attendu') {
        totalSubsidiesPending += s.amount;
        const dateStr = s.prudent_payment_date || s.theoretical_payment_date;
        if (dateStr) {
          const m = new Date(dateStr).getMonth();
          monthly[m].inflows += s.amount;
        }
      }
    });

    let totalLivestockRevenue = 0;
    allSales.forEach((s: AgriLivestockSale) => {
      const rev = s.quantity * s.unit_price;
      totalLivestockRevenue += rev;
      if (s.planned_date) {
        const m = new Date(s.planned_date).getMonth();
        monthly[m].inflows += rev;
      }
    });

    let totalLivestockCharges = 0;
    allLivestockCharges.forEach((ch: AgriLivestockCharge) => {
      totalLivestockCharges += ch.annual_amount;
      const dist = distributeAnnualAmount(ch.annual_amount, ch.monthly_pattern);
      dist.forEach((amt, mi) => { monthly[mi].outflows += amt; });
    });

    const totalEquipmentMonthly = equipment
      .filter((e: AgriEquipment) => e.status === 'finance')
      .reduce((s: number, e: AgriEquipment) => s + (e.monthly_payment || computeMonthlyLoan(e.financed_amount, e.loan_duration_years)), 0);

    monthly.forEach(mo => { mo.outflows += totalEquipmentMonthly; });

    let cum = 0;
    monthly.forEach(mo => {
      mo.net = mo.inflows - mo.outflows;
      cum += mo.net;
      mo.cumulative = cum;
    });

    const maxFlow = Math.max(...monthly.map(m => Math.max(m.inflows, m.outflows)), 1);
    const totalRevenue = totalCropRevenue + totalSubsidiesReceived + totalSubsidiesPending + totalLivestockRevenue;
    const totalCharges = totalCropCharges + totalLivestockCharges + totalEquipmentMonthly * 12;
    const netMargin = totalRevenue - totalCharges;

    return {
      monthly, maxFlow,
      totalRevenue, totalCharges, netMargin,
      totalCropRevenue, totalCropCharges,
      totalSubsidiesReceived, totalSubsidiesPending,
      totalLivestockRevenue, totalLivestockCharges,
      totalEquipmentMonthly,
    };
  }, [campaigns, chargesByCampaign, subsidies, allSales, allLivestockCharges, equipment]);

  const alerts = useMemo(() => {
    const result: string[] = [];
    campaigns.forEach((c: AgriCampaign) => {
      const charges: AgriCampaignCharge[] = chargesByCampaign[c.id] || [];
      const kpi = computeCampaignKPIs(c, charges);
      if (kpi.gross_margin < 0) result.push(`Marge négative : ${c.name}`);
      if (charges.length > 0 && kpi.break_even_price > c.target_price_eur_t * 0.9) {
        result.push(`Seuil de rentabilité proche pour ${c.name}`);
      }
    });
    subsidies.filter((s: AgriSubsidy) => s.status === 'attendu' && s.prudent_payment_date).forEach((s: AgriSubsidy) => {
      const d = new Date(s.prudent_payment_date!);
      const now = new Date();
      if (d < now) result.push(`Aide en retard : ${s.name} (date prudente dépassée)`);
    });
    return result;
  }, [campaigns, chargesByCampaign, subsidies]);

  const hasData = campaigns.length > 0 || subsidies.length > 0 || herds.length > 0 || equipment.length > 0;

  if (!hasData) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
        <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Wheat className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-gray-700 font-medium mb-2">Aucune donnée pour {year}</h3>
        <p className="text-gray-400 text-sm">Commencez par créer une campagne, ajouter des aides ou enregistrer vos troupeaux.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Vue consolidée {year}</h2>
        <p className="text-sm text-gray-500">Recettes, charges et flux de trésorerie de votre exploitation</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wheat className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-gray-500">Cultures</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{formatEur(data.totalCropRevenue)}</p>
          <p className="text-xs text-red-500 mt-0.5">Charges : {formatEur(data.totalCropCharges)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-500">Aides</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{formatEur(data.totalSubsidiesReceived + data.totalSubsidiesPending)}</p>
          <p className="text-xs text-blue-500 mt-0.5">En attente : {formatEur(data.totalSubsidiesPending)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Tractor className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-medium text-gray-500">Élevage</span>
          </div>
          <p className="text-xl font-bold text-gray-900">{formatEur(data.totalLivestockRevenue)}</p>
          <p className="text-xs text-red-500 mt-0.5">Charges : {formatEur(data.totalLivestockCharges)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-4 h-4 text-slate-600" />
            <span className="text-xs font-medium text-gray-500">Annuités mat.</span>
          </div>
          <p className="text-xl font-bold text-gray-900">
            {formatEur(data.totalEquipmentMonthly)}<span className="text-sm font-normal text-gray-500">/mois</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5">{formatEur(data.totalEquipmentMonthly * 12)}/an</p>
        </div>
      </div>

      <div className={`rounded-2xl p-5 border ${data.netMargin >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${data.netMargin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
              Résultat net consolidé {year}
            </p>
            <p className={`text-3xl font-bold mt-1 ${data.netMargin >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
              {formatEur(data.netMargin)}
            </p>
            <p className={`text-sm mt-1 ${data.netMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              Recettes : {formatEur(data.totalRevenue)} · Charges : {formatEur(data.totalCharges)}
            </p>
          </div>
          {data.netMargin >= 0
            ? <TrendingUp className="w-10 h-10 text-emerald-400" />
            : <TrendingDown className="w-10 h-10 text-red-400" />
          }
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">
              {alerts.length} point{alerts.length > 1 ? 's' : ''} d'attention
            </span>
          </div>
          {alerts.map((a, i) => (
            <p key={i} className="text-sm text-amber-700 flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 mt-1.5" />
              {a}
            </p>
          ))}
        </div>
      )}

      {alerts.length === 0 && hasData && (
        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-700 font-medium">
            Aucune alerte — votre exploitation est sur la bonne trajectoire
          </p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Flux de trésorerie mensuel</h3>
        <div className="space-y-2">
          {data.monthly.map(m => (
            <div key={m.month} className="grid grid-cols-[40px_1fr_auto_70px] items-center gap-3">
              <span className="text-xs font-medium text-gray-400 text-right">{m.label}</span>
              <div className="space-y-1">
                <Bar value={m.inflows} max={data.maxFlow} positive={true} />
                <Bar value={m.outflows} max={data.maxFlow} positive={false} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs min-w-[160px]">
                <span className="text-green-600 font-medium text-right">{formatEur(m.inflows)}</span>
                <span className="text-red-500 text-right">{formatEur(m.outflows)}</span>
              </div>
              <span className={`text-xs font-bold text-right ${m.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {m.net >= 0 ? '+' : ''}{formatEur(m.net)}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
          <span className="text-sm text-gray-500">Flux net cumulé sur l'année</span>
          <span className={`text-base font-bold ${data.monthly[11].cumulative >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            {formatEur(data.monthly[11].cumulative)}
          </span>
        </div>
      </div>
    </div>
  );
}
